import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { UserStore } from '../user/store/user.store';
import { PermissionRight } from '../user/models/permission';

/**
 * Schuetzt Routen anhand der Client-Permissions (`GET /{tenantId}/users/permissions`).
 *
 * Konfiguration ueber `route.data`:
 *   - `permissions: string[]`        – benoetigte Permission-Codes (OR-Semantik; leer = frei)
 *   - `permissionMinRight?: PermissionRight` – Mindest-Recht (Default: Read)
 *   - `requireAllPermissions?: boolean`      – true = AND statt OR
 *
 * Async-sicher: wartet, bis die Permissions des aktiven Tenants geladen sind
 * (`userPermissions !== null`), sonst wuerde waehrend des Ladens faelschlich verweigert.
 * Sinnvoll in Kombination mit `TenantReadyGuard` (stellt Tenant + Registrierungen sicher).
 */
export const PermissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const userStore = inject(UserStore);
  const router = inject(Router);

  const required = (route.data['permissions'] as string[]) ?? [];
  const minRight = (route.data['permissionMinRight'] as PermissionRight) ?? PermissionRight.Read;
  const requireAll = route.data['requireAllPermissions'] === true;

  if (required.length === 0) return true;

  return toObservable(userStore.userPermissions).pipe(
    filter(perms => perms !== null),
    take(1),
    map(() => {
      const allowed = requireAll
        ? userStore.hasAllPermissions(required, minRight)
        : userStore.hasAnyPermission(required, minRight);
      return allowed ? true : router.parseUrl('/forbidden');
    }),
  );
};
